import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Gift, Sparkles, Loader2 } from 'lucide-react';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';

export default function Gifts() {
    const { user } = useAuth();
    const [packages, setPackages] = useState([]);
    const [picked, setPicked] = useState('3mo');
    const [recipient, setRecipient] = useState('');
    const [message, setMessage] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [mine, setMine] = useState(null);

    useEffect(() => {
        api.get('/gifts/packages').then((r) => setPackages(r.data.packages || []));
        api.get('/gifts/mine').then((r) => setMine(r.data)).catch(() => {});
    }, []);

    const buy = async (e) => {
        e.preventDefault();
        setError(''); setSubmitting(true);
        try {
            const { data } = await api.post('/gifts/checkout', {
                package_id: picked,
                origin_url: window.location.origin,
                recipient_email: recipient.trim() || null,
                message: message.trim() || null,
            });
            window.location.href = data.url;
        } catch (err) {
            setError(err?.response?.data?.detail || 'Could not start checkout. Please try again.');
            setSubmitting(false);
        }
    };

    return (
        <div className="space-y-8" data-testid="gifts-page">
            <header>
                <p className="text-xs uppercase tracking-[0.25em] text-forest-soft">A gift of stillness</p>
                <h1 className="font-serif text-4xl md:text-5xl text-forest mt-2">Give someone a quieter mind</h1>
                <p className="text-forest-soft mt-2 max-w-xl">
                    Premium for a month or a year — the gift of small, daily quiet. We’ll generate a redemption code for you to share.
                </p>
            </header>

            <form onSubmit={buy} className="qm-card p-7 md:p-9 space-y-6" data-testid="gift-form">
                <div>
                    <label className="text-sm text-forest-soft block mb-2">Choose a duration</label>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="gift-packages">
                        {packages.map((p) => (
                            <button
                                key={p.id}
                                type="button"
                                onClick={() => setPicked(p.id)}
                                data-testid={`pkg-${p.id}`}
                                className={`text-left rounded-2xl border p-4 transition ${
                                    picked === p.id ? 'border-forest bg-sage/30 shadow-sm' : 'border-border hover:bg-sage/15'
                                }`}
                            >
                                <div className="font-serif text-xl text-forest">{p.label}</div>
                                <div className="text-sm text-forest-soft mt-1">${p.amount.toFixed(2)}</div>
                                {p.id === '12mo' && <div className="text-xs text-gold mt-1">Best value</div>}
                            </button>
                        ))}
                    </div>
                </div>

                <div className="grid md:grid-cols-2 gap-5">
                    <div>
                        <label className="text-sm text-forest-soft" htmlFor="gift-recipient">Recipient email (optional)</label>
                        <input
                            id="gift-recipient" type="email" value={recipient}
                            onChange={(e) => setRecipient(e.target.value)}
                            placeholder="friend@example.com"
                            className="qm-input mt-1" data-testid="gift-recipient-input"
                        />
                        <p className="text-xs text-forest-soft mt-1">For now you’ll receive the redemption code to share yourself. Email delivery arrives soon.</p>
                    </div>
                    <div>
                        <label className="text-sm text-forest-soft" htmlFor="gift-message">A short note (optional)</label>
                        <input
                            id="gift-message" type="text" value={message} maxLength={280}
                            onChange={(e) => setMessage(e.target.value)}
                            placeholder="For Sundays in slow light."
                            className="qm-input mt-1" data-testid="gift-message-input"
                        />
                    </div>
                </div>

                {error && (
                    <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-2xl px-4 py-3" data-testid="gift-error">
                        {error}
                    </div>
                )}

                <div className="flex flex-wrap items-center gap-3">
                    <button
                        type="submit" disabled={submitting} data-testid="gift-checkout-button"
                        className="qm-btn-primary inline-flex items-center gap-2 disabled:opacity-60"
                    >
                        {submitting ? <Loader2 size={16} className="animate-spin" /> : <Gift size={16} />}
                        {submitting ? 'Opening checkout…' : 'Continue to checkout'}
                    </button>
                    <span className="text-xs text-forest-soft">Secured by Stripe · test mode</span>
                </div>
            </form>

            {mine && (mine.bought?.length > 0 || mine.redeemed?.length > 0) && (
                <section className="qm-card p-7 md:p-9" data-testid="my-gifts">
                    <h2 className="font-serif text-2xl text-forest mb-4 flex items-center gap-2"><Sparkles size={18} /> Your gifts</h2>
                    {mine.bought?.length > 0 && (
                        <>
                            <h3 className="text-sm text-forest-soft uppercase tracking-wider mb-2">Sent</h3>
                            <ul className="space-y-2 mb-5" data-testid="gifts-bought">
                                {mine.bought.map((g) => (
                                    <li key={g.code} className="rounded-2xl border border-border p-4 flex items-center gap-4">
                                        <div className="font-mono text-forest text-lg tracking-wider">{g.code}</div>
                                        <div className="text-sm text-forest-soft">{g.months} mo · ${g.amount?.toFixed?.(2)}</div>
                                        <div className="ml-auto text-xs">
                                            {g.redeemed_by ? <span className="text-forest">Redeemed</span> : <span className="text-forest-soft">Unredeemed</span>}
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        </>
                    )}
                    {mine.redeemed?.length > 0 && (
                        <>
                            <h3 className="text-sm text-forest-soft uppercase tracking-wider mb-2">Redeemed</h3>
                            <ul className="space-y-2" data-testid="gifts-redeemed">
                                {mine.redeemed.map((g) => (
                                    <li key={g.code} className="rounded-2xl border border-border p-4 flex items-center gap-4">
                                        <div className="font-mono text-forest text-lg tracking-wider">{g.code}</div>
                                        <div className="text-sm text-forest-soft">+{g.months} months premium</div>
                                    </li>
                                ))}
                            </ul>
                        </>
                    )}
                    {mine.premium_until && (
                        <p className="text-sm text-forest-soft mt-5">
                            Your premium runs through <span className="text-forest font-medium">{new Date(mine.premium_until).toLocaleDateString()}</span>.
                        </p>
                    )}
                </section>
            )}

            <p className="text-xs text-forest-soft">
                Have a code? <Link to="/app/profile" className="text-forest underline" data-testid="gifts-redeem-link">Redeem it on your profile</Link>.
            </p>
        </div>
    );
}
